import React, { useState, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import { useFormik, FormikProvider } from "formik";
import {
  Search as SearchIcon,
  FileSpreadsheet,
  ChevronRight,
  Database,
  ChevronLeft,
  Loader2,
  Users,
  RefreshCw,
  AlertCircle,
} from "lucide-react";
import { importEmployees } from "../../store/actions";
import {
  Button,
  Table,
  Breadcrumb,
  useToast,
  Card,
  FileUpload
} from "../UI";
import axios from "axios";
import dayjs from "dayjs";

const EmployeeList = ({
  importEmployees
}: any) => {
  const { toast } = useToast();

  // Pagination & Search States
  const [employees, setEmployees] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(15);
  const [searchVal, setSearchVal] = useState("");
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(0);

  // ERP Sync States
  const [syncStatus, setSyncStatus] = useState<any>(null);
  const [isSyncing, setIsSyncing] = useState(false);

  const fetchSyncStatus = useCallback(async () => {
    try {
      const { data } = await axios.get("/api/employees/sync-status");
      setSyncStatus(data);
      if (data.status === "SYNCING") {
        setIsSyncing(true);
      }
    } catch (err) {
      console.error("Failed to fetch ERP sync status:", err);
    }
  }, []);

  const handleSyncNow = async () => {
    setIsSyncing(true);
    try {
      await axios.post("/api/employees/sync");

      let attempts = 0;
      const interval = setInterval(async () => {
        try {
          const { data } = await axios.get("/api/employees/sync-status");
          setSyncStatus(data);
          attempts++;
          if (data.status !== "SYNCING" || attempts > 20) {
            clearInterval(interval);
            setIsSyncing(false);
            if (data.status === "SUCCESS") {
              toast("ERP sync completed successfully. Employee database updated.", "success");
              fetchEmployees(currentPage, searchVal);
            } else if (data.status === "NOT_CONFIGURED") {
              toast("ERP connection unavailable. Contact your administrator.", "error");
            } else if (data.status === "FAILED") {
              toast(`Sync failed: ${data.errorMessage || 'ERP server unreachable'}`, "error");
            }
          }
        } catch (err) {
          clearInterval(interval);
          setIsSyncing(false);
        }
      }, 2000);
    } catch (err: any) {
      toast(err?.response?.data?.message || "Failed to trigger sync", "error");
      setIsSyncing(false);
    }
  };

  useEffect(() => {
    fetchSyncStatus();
  }, [fetchSyncStatus]);

  const fetchEmployees = useCallback(async (page: number, search: string) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`/api/employees/getallemployees`, {
        params: {
          page,
          limit: pageSize,
          search: search || undefined
        }
      });
      setEmployees(data.data);
      setTotalCount(data.total);
      setTotalPages(data.totalPages);
    } catch (error) {
      console.error("Error fetching employees:", error);
      toast("Failed to load employees", "error");
    } finally {
      setLoading(false);
    }
  }, [pageSize, toast]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchVal !== "") {
        setCurrentPage(1);
        fetchEmployees(1, searchVal);
      } else {
        fetchEmployees(currentPage, "");
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchVal, currentPage, fetchEmployees]);

  const columns = [
    {
      title: "Full Name",
      key: "fullName",
      render: (val: any) => <span className="font-medium text-slate-800 whitespace-nowrap capitalize">{val?.toLowerCase()}</span>
    },
    {
      title: "Oracle ID",
      key: "oracleId",
      render: (val: any) => <span className="font-normal text-slate-500 whitespace-nowrap">{val}</span>
    },
    {
      title: "Division",
      key: "division",
      render: (val: any) => <span className="font-normal text-slate-600 whitespace-nowrap">{val}</span>
    },
    {
      title: "Category",
      key: "locationCategory",
      render: (val: any) => (
        <span className="inline-flex items-center px-2 py-0.5 text-slate-500 text-[12px] font-medium  tracking-wider uppercase whitespace-nowrap">
          {val?.toLowerCase() === "head quarter" ? "HQ" : val}
        </span>
      )
    },
    {
      title: "Job Role",
      key: "jobRole",
      render: (val: any) => (
        <div
          className="font-normal text-slate-600 overflow-hidden truncate whitespace-nowrap max-w-[220px]"
          title={val}
        >
          {val || <span className="text-slate-300">—</span>}
        </div>
      )
    }
  ];

  const formik = useFormik({
    initialValues: {
      employee: null,
    },
    onSubmit: async (values, { setErrors, resetForm }) => {
      try {
        if (!values.employee) {
          toast("Please select a file first", "error");
          return;
        }
        await importEmployees(values);
        toast("Employee data imported successfully", "success");
        resetForm({});
        fetchEmployees(currentPage, searchVal); // Refresh data
      } catch (error: any) {
        toast(error.message || "Upload failed", "error");
        setErrors({ employee: error.message || "Upload failed" });
      }
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2">
        <Breadcrumb
          items={[
            { label: 'Home', to: '/dashboard' },
            { label: 'Employees' }
          ]}
        />
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 bg-[#8dc63f]/15 rounded-xl flex items-center justify-center">
              <Users className="w-4 h-4 text-[#8dc63f]" />
            </div>
            <h1 className="text-xl font-bold text-slate-900 tracking-tight leading-none">Employee Directory</h1>
          </div>

          <div className="relative group">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-[#8dc63f] transition-colors" />
            <input
              type="text"
              placeholder="Search by name, ID, or division..."
              value={searchVal}
              onChange={(e) => setSearchVal(e.target.value)}
              className="pl-9 pr-3 h-8 bg-slate-50 border border-slate-100 rounded-lg text-xs font-normal focus:bg-white focus:border-[#8dc63f] focus:ring-2 focus:ring-[#8dc63f]/5 transition-all outline-none w-full sm:w-64"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-4">

          <Card className="overflow-hidden flex flex-col p-0">
            <div className="p-3 border-b border-slate-100 flex items-center justify-end bg-white z-10">

              <div className="flex items-center">
                {loading && <Loader2 className="w-4 h-4 text-[#8dc63f] animate-spin" />}
                <span className="px-3 text-[#8dc63f] text-xs font-black tracking-widest uppercase">
                  {totalCount} Total
                </span>
              </div>
            </div>

            <div className="min-h-[400px] w-full overflow-x-auto scrollbar-bold scrollbar-thumb-slate-400">
              <Table
                columns={columns}
                dataSource={employees}
                rowKey="oracleId"
                loading={loading}
              />
            </div>

            {/* Pagination Controls */}
            <div className="p-4 bg-slate-50/50 border-t border-slate-100 flex items-center justify-between">
              <div className="text-xs font-medium text-slate-400 uppercase tracking-widest">
                Page {currentPage} of {totalPages || 1}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1 || loading}
                  className="h-9 px-3 border-slate-200"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Prev
                </Button>
                <div className="flex items-center gap-1 mx-2">
                  {/* Simple page numbers could go here, but with potentially thousands of pages, Next/Prev is safer */}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage >= totalPages || loading}
                  className="h-9 px-3 border-slate-200"
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          </Card>
        </div>

        <div className="lg:col-span-1">
          <div className="sticky space-y-6">
            <Card className="overflow-hidden flex flex-col p-0">
              <h3 className="px-5 font-bold text-slate-800 tracking-tight">Bulk Import</h3>

              <div className="p-5 space-y-6">
                <FormikProvider value={formik}>
                  <form onSubmit={formik.handleSubmit} className="space-y-6">
                    <FileUpload
                      accept=".csv"
                      onFileSelect={(file) => formik.setFieldValue("employee", file)}
                      error={formik.touched.employee ? formik.errors.employee as string : ""}
                      helperText="Only .csv files accepted"
                      maxSize={5}
                      required
                    />

                    <Button
                      type="submit"
                      className="w-full h-10 gap-2 text-xs"

                      disabled={formik.isSubmitting}
                    >
                      <FileSpreadsheet className="w-4 h-4" />
                      {formik.isSubmitting ? 'Importing...' : 'Sync Database'}
                    </Button>
                  </form>
                </FormikProvider>
              </div>
            </Card>

            <Card className="p-2">
              {/* Header row: title + status dot */}
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-extrabold text-slate-500 uppercase tracking-widest">ERP Auto Sync</span>
                <span className={`w-2 h-2 rounded-full ${syncStatus?.status === "SUCCESS" ? "bg-[#8dc63f]" :
                  syncStatus?.status === "SYNCING" ? "bg-amber-400 animate-pulse" :
                    (syncStatus?.status === "FAILED" || syncStatus?.status === "NOT_CONFIGURED") ? "bg-rose-400" :
                      "bg-slate-300"
                  }`} />
              </div>

              {/* Last sync line */}
              <p className="text-xs text-slate-400 mb-1">
                Last synced:{" "}
                <span className="font-semibold text-slate-600">
                  {syncStatus?.lastSyncAt
                    ? dayjs(syncStatus.lastSyncAt).format("MMM D, YYYY [at] h:mm A")
                    : "Never"}
                </span>
              </p>

              {/* User-friendly message for any unavailable state */}
              {(syncStatus?.status === "FAILED" || syncStatus?.status === "NOT_CONFIGURED") && (
                <p className="text-[11px] text-rose-400 font-medium mb-3">
                  ERP connection unavailable. Contact your administrator.
                </p>
              )}

              <Button
                onClick={handleSyncNow}
                disabled={isSyncing}
                variant="primary"
                className={`w-full h-10 mt-2 gap-2 text-xs border-slate-200 hover:border-[#8dc63f]/30 hover:bg-[#8dc63f] ${isSyncing ? "text-slate-400 bg-slate-50" : "text-slate-600"}`}
              >
                <RefreshCw className={`w-3 h-3 ${isSyncing ? "animate-spin text-slate-400" : "text-slate-400"}`} />
                {isSyncing ? "Syncing..." : "Sync Now"}
              </Button>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapDispatchToProps = {
  importEmployees
};

export default connect(null, mapDispatchToProps)(EmployeeList);



